import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import AdminDashboard from './components/AdminDashboard';
import ReportView from './components/ReportView';
import PlayerQuiz from './components/PlayerQuiz';
import PlayerGate from './components/PlayerGate';
import AdminGate from './components/AdminGate';
import MySessions from './components/MySessions';
import ToastContainer from './components/ToastContainer';
import app, { auth, functions, getFunctionsBaseUrl } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { getUserRole, initializeUserManagementListeners, ensureDefaultAdminInFirestore } from './utils/firebaseAuth';
import { getPreviewRole } from './utils/previewRole';
import { getSessionById, quizDurationToSeconds, normalizeQuizDurationMinutes } from './utils/quizSessionManager';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quizUrl, setQuizUrl] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [sessionTitle, setSessionTitle] = useState(null);
  const [timeLimit, setTimeLimit] = useState(() => {
    try {
      const durationParam = new URLSearchParams(window.location.search).get('duration');
      return quizDurationToSeconds(durationParam);
    } catch {
      return quizDurationToSeconds(null);
    }
  });
  const [player, setPlayer] = useState(null);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    initializeUserManagementListeners();
  }, []);

  useEffect(() => {
    if (user?.email) {
      ensureDefaultAdminInFirestore(); // Run when signed in so Firestore writes succeed (rules may require auth)
    }
  }, [user?.email]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const quizParam = urlParams.get('quiz');
    const sessionParam = urlParams.get('session');

    if (quizParam) {
      setQuizUrl(decodeURIComponent(quizParam));
    }
    if (sessionParam) {
      setSessionId(decodeURIComponent(sessionParam));
    }
    const durationParam = urlParams.get('duration');
    if (durationParam) {
      setTimeLimit(quizDurationToSeconds(durationParam));
    }

    const unsubscribe = onAuthStateChanged(auth, (authUser) => {
      setUser(authUser);
      setPlayer(authUser);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!sessionId) return undefined;
    getSessionById(sessionId).then((session) => {
      if (cancelled || !session) return;
      if (session.title || session.quizFileName) {
        setSessionTitle(session.title || session.quizFileName);
      }
      if (session.durationMinutes != null) {
        const minutes = normalizeQuizDurationMinutes(session.durationMinutes);
        setTimeLimit(quizDurationToSeconds(minutes));
        try {
          const params = new URLSearchParams(window.location.search);
          params.set('duration', String(minutes));
          const qs = params.toString();
          window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
        } catch {
          /* ignore */
        }
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [sessionId]);

  useEffect(() => {
    let cancelled = false;
    if (!user?.email) {
      setUserRole(null);
      return;
    }
    getUserRole(user.email).then((role) => {
      if (!cancelled) setUserRole(getPreviewRole() || role);
    });
    return () => { cancelled = true; };
  }, [user?.email]);

  // Sync admin custom claim from Firestore so Storage rules allow uploads for Firestore admins
  useEffect(() => {
    if (!user || !(userRole === 'admin' || userRole === 'consultant' || userRole === 'support')) return;
    if (!app || !auth?.currentUser || !functions) return;
    const doSync = async () => {
      const token = await auth.currentUser.getIdToken(true);
      const baseUrl = getFunctionsBaseUrl();
      try {
        const syncAdminClaims = httpsCallable(functions, 'syncAdminClaims');
        const res = await syncAdminClaims();
        if (process.env.NODE_ENV === 'development' && res?.data) {
          console.debug('syncAdminClaims:', res.data);
        }
        const tokenResult = await auth.currentUser.getIdTokenResult();
        if (process.env.NODE_ENV === 'development' && tokenResult?.claims) {
          console.debug('token claims after syncAdminClaims:', tokenResult.claims);
        }
      } catch (callableErr) {
        if (baseUrl && token) {
          try {
            const r = await fetch(`${baseUrl}/syncAdminClaimsHttp`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ data: {} }),
            });
            if (!r.ok) throw new Error(r.statusText);
            const json = await r.json();
            if (json?.result && process.env.NODE_ENV === 'development') {
              console.debug('syncAdminClaims (HTTP fallback):', json.result);
            }
            await auth.currentUser.getIdToken(true);
          } catch (httpErr) {
            console.warn('syncAdminClaims failed:', callableErr?.message || callableErr, '; HTTP fallback:', httpErr?.message || httpErr);
          }
        } else {
          console.warn('syncAdminClaims failed:', callableErr?.message || callableErr);
        }
      }
    };
    doSync();
  }, [user?.uid, userRole]);

  const handlePlayerLogin = (playerUser) => {
    setPlayer(playerUser);
  };

  const handleSelectSession = (session) => {
    setQuizUrl(session?.quizFileUrl);
    setSessionId(session?.id || null);
    setSessionTitle(session?.title || session?.quizFileName || 'Quiz');
    setTimeLimit(quizDurationToSeconds(session?.durationMinutes));
  };

  // Admin/consultant/support access from Firestore — or local UI preview via ?previewRole=
  const previewRole = getPreviewRole();
  const effectiveRole = previewRole || userRole;
  const portalHome = effectiveRole === 'consultant' ? '/quiz' : '/dashboard';
  const canAccessAdminArea =
    userRole === 'admin' ||
    userRole === 'consultant' ||
    userRole === 'support' ||
    !!previewRole;

  const location = useLocation();
  if (location.pathname === '/report-view') {
    return <ReportView />;
  }

  if (loading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Player mode: quiz URL present (from link or from selecting a session)
  if (quizUrl) {
    const quizPlayer = player || (previewRole
      ? { uid: 'preview', email: 'preview@local.test', displayName: 'Preview Player' }
      : null);
    return (
      <>
        <ToastContainer />
        <PlayerGate onPlayerLogin={handlePlayerLogin}>
          {quizPlayer && (
            <PlayerQuiz
              quizUrl={quizUrl}
              sessionId={sessionId}
              sessionTitle={sessionTitle}
              player={quizPlayer}
              timeLimit={timeLimit}
              isPlayerMode={true}
              isAdmin={userRole === 'admin' || previewRole === 'admin'}
              canReview={userRole === 'admin' || userRole === 'support' || previewRole === 'admin' || previewRole === 'support'}
            />
          )}
        </PlayerGate>
      </>
    );
  }

  // No quiz URL: show login when not signed in (unless local role preview is active)
  if (!user && !previewRole) {
    return (
      <>
        <ToastContainer />
        <AdminGate />
      </>
    );
  }

  if (canAccessAdminArea) {
    return (
      <>
        <ToastContainer />
        {previewRole && (
          <div className="preview-role-banner">
            Previewing as: <strong>{previewRole}</strong>
            <a href={`${portalHome}?previewRole=off`} className="preview-role-exit">Exit preview</a>
          </div>
        )}
        <AdminGate>
          <Routes>
            <Route path="/" element={<Navigate to={portalHome} replace />} />
            <Route
              path="/dashboard"
              element={effectiveRole === 'consultant' ? <Navigate to={portalHome} replace /> : <AdminDashboard />}
            />
            <Route path="/quiz" element={<AdminDashboard />} />
            <Route path="/users" element={<AdminDashboard />} />
            <Route path="/reports" element={<AdminDashboard />} />
            <Route
              path="/calendar"
              element={effectiveRole === 'consultant' ? <Navigate to={portalHome} replace /> : <AdminDashboard />}
            />
            <Route path="/timesheets" element={<AdminDashboard />} />
            <Route path="/profile" element={<AdminDashboard />} />
            <Route path="*" element={<Navigate to={portalHome} replace />} />
          </Routes>
        </AdminGate>
      </>
    );
  }

  // Signed in but not admin - show My Sessions (only assigned users see their sessions)
  return (
    <>
      <ToastContainer />
      <PlayerGate onPlayerLogin={handlePlayerLogin}>
        <MySessions user={user} onSelectSession={handleSelectSession} />
      </PlayerGate>
    </>
  );
}

export default App;
